import type { ScopedThreadRef, SnapShotSource } from "@afar-research/contracts";

import {
  type DraftId,
  type PersistedComposerImageAttachment,
  useComposerDraftStore,
} from "../composerDraftStore";

export type SnapShotDraftTarget = DraftId | ScopedThreadRef;

/**
 * Attaches a SnapShot to a draft and saves it with the draft, so it survives a reload like any
 * other attachment. Attaching the same capture twice is a no-op. Throws when the draft is full
 * or the attachment could not be saved.
 */
export async function attachSnapShotToDraft(
  target: SnapShotDraftTarget,
  capture: {
    readonly id: string;
    readonly file: File;
    readonly dataUrl: string;
    readonly source: SnapShotSource;
  },
): Promise<void> {
  const store = useComposerDraftStore.getState();
  const { id, file, dataUrl, source } = capture;
  const alreadyAttached = store.getComposerDraft(target)?.images.some((image) => image.id === id);
  if (
    !alreadyAttached &&
    !store.addImage(target, {
      type: "image",
      id,
      name: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      previewUrl: dataUrl,
      file,
      source,
    })
  ) {
    throw new Error("Remove an attachment, then try this capture again.");
  }
  const persisted: PersistedComposerImageAttachment = {
    id,
    name: file.name,
    mimeType: file.type,
    sizeBytes: file.size,
    dataUrl,
    source,
  };
  const persistedAttachments =
    store
      .getComposerDraft(target)
      ?.persistedAttachments.filter((attachment) => attachment.id !== id) ?? [];
  await store.syncPersistedAttachments(target, [...persistedAttachments, persisted]);
  if (
    !store.getComposerDraft(target)?.persistedAttachments.some((attachment) => attachment.id === id)
  ) {
    throw new Error("The capture could not be saved to the draft.");
  }
}
